// src/pages/DocumentView.tsx
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase'; // Your centralized exports
import DOMPurify from 'dompurify';

const DocumentView = () => {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!docId) {
        setError('No document ID in URL');
        setLoading(false);
        return;
      }
      
      const user = getAuth().currentUser;
      if (!user) {
        setError('You must be logged in to view this document.');
        setLoading(false);
        navigate('/auth/signon'); // Redirect to login
        return;
      }

      try {
        console.log('[VIEW FETCH] Loading doc ID:', docId);
        console.log('[VIEW AUTH] Current UID:', auth?.currentUser?.uid || 'Not signed in');

        // Client-side check (Firestore rules will also enforce this)
        const idTokenResult = await user.getIdTokenResult(true);
        const isAdmin = idTokenResult.claims.admin === true;

        const docRef = doc(db, 'documents', docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title || 'Untitled');
          setContent(data.content || '<p>No content available</p>');

          // Further client-side authorization check
          if (data.authorId !== user.uid && !isAdmin) {
            setError('You do not have permission to view this document.');
            setLoading(false);
            return;
          }
        } else {
          setError('Document not found');
        }
      } catch (err: unknown) {
        console.error('Fetch failed in DocumentView:', err);
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to load document: ${msg}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        Loading document...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-red-600">
        <p>{error}</p>
        <button
          onClick={() => navigate('/author')}
          className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Back to Author Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 prose">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold">{title}</h1>
          <p className="text-sm text-gray-500 mt-1 font-mono">
            ID: {docId}
          </p>
        </div>

        <Link
          to={`/author/${docId}/edit`}
          className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          Edit Document
        </Link>
      </div>

      <div
        className="prose max-w-none"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
      />
    </div>
  );
};

export default DocumentView;